import { League, Manager } from '@/models/yahoo';
import { DisplayWeeklyTable, CategoryOverallTable, CategoryCompareTable, RosterStrengthTable, InjuryAlert } from '@/models/toolbox';
import axios, { AxiosResponse } from 'axios'

// const Cookies = require('js-cookie');
// const creds = Cookies.get('fc_cr')

export interface ApiService {
  setCredentials(access_token: string, id_token: string, firebase_idtoken: string);
  getManager(): Promise<Manager>;
  getLeagues(force_refresh: boolean): Promise<Array<League>>;
  getWeeks(league_key: string, force_refresh: boolean): Promise<Array<DisplayWeeklyTable>>;
  getCategoryWins(league_key: string, force_refresh: boolean): Promise<CategoryOverallTable>;
  getCategoryCompare(league_key: string, force_refresh: boolean): Promise<Array<CategoryCompareTable>>;

  getAlertsInjuryConfig(): Promise<Array<InjuryAlert>>
  setAlertsInjuryConfig(alert: InjuryAlert): Promise<InjuryAlert>
}

enum Endpoints {
  getManager = "fantasy/manager",
  getWeeks = "fantasy/weeks",
  getLeagues = "fantasy/leagues",
  getCategoryWins = "fantasy/categoryWins",
  getCategoryCompare = "fantasy/categoryCompare",
  getRosterCategoryCompare = "fantasy/rosterCategoryCompare",
  alertsInjuryConfig = "alerts/injuryConfig"
}

export class ToolboxApiService implements ApiService {
  private access_token?: string;
  private id_token?: string;
  private firebase_idtoken?: string;

  setCredentials(access_token: string, id_token: string, firebase_idtoken: string) {
    this.access_token = access_token;
    this.id_token = id_token;
    this.firebase_idtoken = firebase_idtoken;
  }

  getRootApi() {
    if (window.location.origin.startsWith("http://localhost:")) {
      // return "https://www.fantasyfalcon.com/api/";
      return "http://localhost:5001/fantasy-companion/us-central1/"
    }
    else {
      //let hosting take care of redirecting to functions
      return window.location.origin + "/api/"
    }
  }

  executeGet(api: string, params?: any, headers?: any) : Promise<AxiosResponse<any>> {
    return axios.get(this.getRootApi() + api, {
      params: params,
      headers: {...headers, ... {
        yahoo_access_token: this.access_token,
        yahoo_id_token: this.id_token,
        firebase_idtoken: this.firebase_idtoken,
      }}
    })
  }

  executePost(api: string, body: any, params?: any, headers?: any) : Promise<AxiosResponse<any>> {
    return axios.post(this.getRootApi() + api,body, {
      params: params,
      headers: {...headers, ... {
        yahoo_access_token: this.access_token,
        yahoo_id_token: this.id_token,
        firebase_idtoken: this.firebase_idtoken,
      }}
    })
  }

  getManager(): Promise<Manager> {
    return this.executeGet(Endpoints.getManager).then( r => {
      return r.data as Manager;
    })
  }

  getLeagues(force_refresh: boolean): Promise<Array<League>> {
    //Temporarily until force refresh button
    if(!force_refresh) {
      force_refresh = this.shouldForceRefresh(Endpoints.getLeagues)
    }

    return this.executeGet(Endpoints.getLeagues, { force_refresh: force_refresh }).then( r => {
      return r.data as Array<League>
    });
  }

  getWeeks(league_key: string, force_refresh: boolean): Promise<Array<DisplayWeeklyTable>> {
    if(!force_refresh) {
      force_refresh = this.shouldForceRefresh(Endpoints.getWeeks)
    }
    return this.executeGet(
      Endpoints.getWeeks,
      { league_key: league_key, force_refresh: force_refresh })
      .then( r => {
        return r.data as Array<DisplayWeeklyTable>
    });
  }

  getCategoryWins(league_key: string, force_refresh: boolean): Promise<CategoryOverallTable> {
    if(!force_refresh) {
      //On purpose as Endpoints.getWeeks since data is dependant on getWeeks on the backend side
      force_refresh = this.shouldForceRefresh(Endpoints.getWeeks)
    }
    return this.executeGet(
      Endpoints.getCategoryWins,
      { league_key: league_key, force_refresh: force_refresh })
      .then( r => {
        return r.data as CategoryOverallTable
    });
  }

  getCategoryCompare(league_key: string, force_refresh: boolean): Promise<Array<CategoryCompareTable>> {
    if(!force_refresh) {
      //On purpose as Endpoints.getWeeks since data is dependant on getWeeks on the backend side
      force_refresh = this.shouldForceRefresh(Endpoints.getWeeks)
    }
    return this.executeGet(
      Endpoints.getCategoryCompare,
      { league_key: league_key, force_refresh: force_refresh })
      .then( r => {
        return r.data as Array<CategoryCompareTable>;
    });
  }

  getRosterCategoryCompare(league_key: string, force_refresh: boolean): Promise<Array<RosterStrengthTable>> {
    if(!force_refresh) {
      //On purpose as Endpoints.getWeeks since data is dependant on getWeeks on the backend side
      force_refresh = this.shouldForceRefresh(Endpoints.getWeeks)
    }
    return this.executeGet(
      Endpoints.getRosterCategoryCompare,
      { league_key: league_key, force_refresh: force_refresh })
      .then( r => {
        return r.data as Array<RosterStrengthTable>;
    });
  }

  //Temporary until refresh button - this has a side effect it also sets the dataRefreshDate so
  shouldForceRefresh(endpoint:string) : boolean {
    //isNaN doesn't work on dates for typescript
    let shouldRefresh = false;
    const dateDataRefresh = Date.parse(localStorage.getItem("dateDataRefresh_" + endpoint) || "invalid")
    if(isNaN(dateDataRefresh)){
      console.log("no data refresh date - setting one now")
      shouldRefresh = true
    }
    else {
      const hoursDiff = (new Date().getTime() - dateDataRefresh) / 1000 / 60 / 60
      if (hoursDiff > 12) {
        console.log("last refresh above 12 hours - refreshing now");
        shouldRefresh = true
      }
    }
    if(shouldRefresh) {
      localStorage.setItem("dateDataRefresh_" + endpoint, new Date().toUTCString())
    }
    return shouldRefresh;
  }

  getAlertsInjuryConfig(): Promise<Array<InjuryAlert>> {
    return this.executeGet(Endpoints.alertsInjuryConfig)
      .then( r => {
        return r.data as Array<InjuryAlert>;
    });
  }

  setAlertsInjuryConfig(alert: InjuryAlert): Promise<InjuryAlert> {
    return this.executePost(Endpoints.alertsInjuryConfig, alert)
    .then( r => {
      return Promise.resolve(r.data as InjuryAlert);
    });
  }
}